import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Route: /property-management
// Add to App.jsx or router file:
//   import PropertyManagementPage from "./pages/PropertyManagementPage";
//   <Route path="/property-management" element={<PropertyManagementPage />} />
// Local dev: http://localhost:3000/property-management

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a",
  red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:      <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:     <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    CheckCircle:    <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Building2:      <><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 0-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></>,
    LayoutDashboard:<><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></>,
    Users:          <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    User:           <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    MessageCircle:  <><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    Repeat:         <><path d="m17 2 4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></>,
    MapPin:         <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Clock:          <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    FileText:       <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
    ShieldCheck:    <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:     <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Star:           <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Wrench:         <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Bell:           <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></>,
    Bookmark:       <><path d="m19 21-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></>,
    Phone:          <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    TrendingUp:     <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Zap:            <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Calendar:       <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    AlertCircle:    <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    ChevronDown:    <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:      <><path d="m18 15-6-6-6 6"/></>,
    Home:           <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Key:            <><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></>,
    Lock:           <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    BarChart2:      <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    Briefcase:      <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    CreditCard:     <><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Pain points (the "before" state) ── */
const PAIN_POINTS = [
  {
    icon: "Phone",
    label: "Chasing Pros by phone",
    desc: "You call three numbers before someone picks up. They say they will come Tuesday. They do not come Tuesday.",
  },
  {
    icon: "AlertCircle",
    label: "Tenants calling you directly",
    desc: "Every maintenance issue routes through your personal number. Evenings, weekends, it does not stop.",
  },
  {
    icon: "FileText",
    label: "No record of what was done",
    desc: "A job was completed months ago. Now there is a dispute. You have a WhatsApp message and nothing else.",
  },
  {
    icon: "Users",
    label: "Different Pros for every property",
    desc: "No continuity. Each property has a different plumber, a different electrician. Quality is inconsistent.",
  },
];

/* ── Core features ── */
const FEATURES = [
  {
    icon: "Bell",
    label: "Tenant maintenance request handling",
    headline: "Tenants submit requests. You approve. A Pro is booked.",
    body: "Give each property its own tenant-facing request link. Tenants describe the issue and submit. You receive a notification, review the request in your dashboard, and approve booking with one tap. The tenant is automatically updated when the Pro is confirmed, when they are on the way, and when the job is done. You never have to relay information manually.",
    detail: [
      "Tenant submits request via a unique property link",
      "You receive an instant notification with full details",
      "One-tap approval triggers automatic Pro matching",
      "Tenant gets SMS updates at every stage",
      "Completed job generates a timestamped record on your dashboard",
    ],
  },
  {
    icon: "Building2",
    label: "Multi-unit booking and tracking",
    headline: "Every property. Every job. One dashboard.",
    body: "All your properties live in a single account, each labelled, addressable, and filterable. Book a plumber for Unit 4B in Kilimani and a painter for the whole block in Nyali in the same session. Every booking is pinned to its property. Filter your dashboard by location, service type, status, or date. Nothing gets confused and nothing gets lost.",
    detail: [
      "Unlimited properties and locations under one account",
      "Each property has its own booking history and job records",
      "Filter dashboard by property, service type, date, or status",
      "Book across multiple properties in a single session",
      "Downloadable per-property maintenance logs",
    ],
  },
  {
    icon: "Bookmark",
    label: "Preferred Pro assignment per property",
    headline: "The same trusted Pro, every time, for every property.",
    body: "When a JiraniPro does excellent work at one of your properties, mark them as preferred for that location. Future bookings for that property will automatically prioritise your preferred Pro. If they are unavailable, you are shown an equally vetted alternative. You build real working relationships with reliable professionals instead of starting from scratch with every job.",
    detail: [
      "Mark any verified JiraniPro as preferred for a specific property",
      "Preferred Pros are matched first on all future bookings for that unit",
      "If unavailable, a qualified alternative is suggested with your approval",
      "Preferred Pro list is visible and editable per property",
      "Build a roster of trusted professionals across your portfolio",
    ],
  },
  {
    icon: "Repeat",
    label: "Scheduled recurring maintenance",
    headline: "Set it once. It runs every month without you.",
    body: "Schedule routine maintenance to repeat automatically. Monthly gutter checks, fortnightly communal area cleaning, quarterly generator servicing. Select the service, the property, the frequency, and confirm the preferred JiraniPro. The job recurs on schedule, the Pro is notified automatically, and you receive a completion report after each visit. No manual rebooking, no missed cycles.",
    detail: [
      "Set any job to repeat weekly, fortnightly, monthly, or quarterly",
      "Assign a preferred Pro for the recurring job",
      "Automatic Pro notification before each scheduled visit",
      "Post-completion report sent to your dashboard after every cycle",
      "Pause, edit, or cancel a recurring schedule any time",
    ],
  },
];

/* ── How it works steps ── */
const HOW_IT_WORKS = [
  {
    num: "01", icon: "Briefcase",
    title: "Create your property management account",
    desc: "Sign up under JiraniApp for Business and select the property management account type. Add your business name, contact number, and billing email. You are set up within minutes and your 14-day trial begins immediately.",
    chips: ["Under 5 minutes", "No billing until trial ends", "Instant dashboard access"],
  },
  {
    num: "02", icon: "Building2",
    title: "Add your properties and units",
    desc: "Add each property you manage as a named location in your dashboard. Include the address, any access notes, and the number of units if applicable. You can add an unlimited number of properties and organise them by estate, city, or however suits your portfolio.",
    chips: ["Unlimited properties", "Per-unit labels supported", "Access notes stored per property"],
  },
  {
    num: "03", icon: "Bell",
    title: "Set up tenant request links",
    desc: "Each property generates a unique tenant request link. Share it with your tenants via WhatsApp, SMS, or printed on a notice in the building. When a tenant submits a maintenance request, it lands directly in your dashboard for review. No more calls to your personal number.",
    chips: ["Unique link per property", "Share via WhatsApp or SMS", "All requests in one inbox"],
  },
  {
    num: "04", icon: "Repeat",
    title: "Schedule recurring maintenance",
    desc: "Go through your portfolio and set up recurring jobs for standard maintenance tasks. Cleaning of communal areas, garden upkeep, pest control, water tank servicing. Assign your preferred JiraniPro to each recurring schedule and confirm. They run automatically from here.",
    chips: ["Weekly, fortnightly, or monthly", "Preferred Pro locked in per schedule", "Automatic reminders before each visit"],
  },
  {
    num: "05", icon: "LayoutDashboard",
    title: "Manage everything from the dashboard",
    desc: "Your dashboard shows live job status across every property. Approve tenant requests, track ongoing work, review completed jobs, and access the full maintenance history for any unit. Filter by property, date, or service category. Share access with your team members at the appropriate permission level.",
    chips: ["Live status across all properties", "Full job history per unit", "Team access with role controls"],
  },
  {
    num: "06", icon: "FileText",
    title: "Receive your monthly invoice",
    desc: "At the end of each billing cycle you receive one consolidated invoice covering every job across your entire portfolio. Each line item shows the date, property, unit, service type, JiraniPro name, and amount. Download as PDF for your accounts or property owners.",
    chips: ["One invoice for all properties", "Itemised by property and unit", "PDF download for accounts"],
  },
];

/* ── Testimonials ── */
const TESTIMONIALS = [
  {
    name: "Patricia N.",
    role: "Property Manager",
    location: "Kilimani, Nairobi",
    units: "34 units",
    text: "I manage 34 rental units across four buildings and I used to spend half my day fielding calls from tenants about broken taps and faulty lights. Since setting up tenant request links on JiraniApp, those calls have stopped. Tenants submit through the link, I approve from my phone, and the Pro handles it. I see a completion report the same day.",
    stat: "34 units managed",
  },
  {
    name: "Daniel O.",
    role: "Landlord and developer",
    location: "Westlands and Ruaka",
    units: "12 units",
    text: "The preferred Pro feature changed how I work. I found a reliable plumber and electrician in Westlands. Now every job at those properties goes to the same two people. They know the buildings, the tenants know them, and the quality has been consistent for eight months.",
    stat: "12 units managed",
  },
  {
    name: "Amina K.",
    role: "Facilities manager",
    location: "Upper Hill, Nairobi",
    units: "1 commercial block",
    text: "We manage a commercial building with 20 office tenants. Maintenance used to be a constant back-and-forth between us, the tenants, and contractors. Now tenants submit requests, we approve, and we have a full audit trail for every job in case a dispute comes up with a tenant later. The invoice alone saves our accounts team hours each month.",
    stat: "1 commercial block, 20 tenants",
  },
];

/* ── FAQs ── */
const FAQS = [
  {
    q: "How does the tenant request link work in practice?",
    a: "Each property in your account generates a unique URL. Share it with your tenants however you prefer: a WhatsApp message, an SMS, or printed on a communal notice. When a tenant opens the link they see a short form asking them to describe the issue, which unit they are in, and upload a photo if helpful. The request lands in your dashboard where you review it and either approve it for booking or decline it with a note back to the tenant. You are in full control at every stage.",
  },
  {
    q: "Can tenants book JiraniPros directly, or does everything go through me?",
    a: "Tenants cannot book directly. The tenant request link only allows them to submit a maintenance request. All booking decisions rest with you as the property manager. This keeps control with you, ensures the right Pro is matched, and means all costs go through your account rather than being paid ad hoc by individual tenants.",
  },
  {
    q: "What happens if my preferred Pro is unavailable for a recurring job?",
    a: "If your preferred Pro is unavailable for a scheduled recurring job, you will be notified in advance and shown the next available alternative from our business-tier Pro pool. You can approve the substitute or reschedule. For recurring schedules, we try to give you at least 48 hours notice if a preferred Pro cannot make a visit.",
  },
  {
    q: "Can I give my team members access to specific properties only?",
    a: "On Enterprise accounts, you can restrict team members to specific properties or locations within your portfolio. On Business plan accounts, team members with booking permissions can access all properties under the account. If you need granular property-level access control, Enterprise is the right plan.",
  },
  {
    q: "Is there a limit on how many tenant request links I can create?",
    a: "No. You can create a unique link for every property, every block, or even every unit in your portfolio. There is no limit on the number of active links. All requests from all links arrive in the same unified dashboard inbox, each labelled with the property it came from.",
  },
  {
    q: "How are maintenance job records kept for audit or legal purposes?",
    a: "Every completed job generates an automatic record that includes the date and time of booking, the arrival and completion timestamps, the JiraniPro name and verified ID, the job description, any photos uploaded by the Pro after completion, and the amount charged. These records are permanently stored in your account and can be filtered by property and downloaded as PDF at any time.",
  },
  {
    q: "Can I add a property owner or client to view jobs for their property?",
    a: "Yes. You can invite external stakeholders as read-only viewers on your account. A property owner can be given access to see job history, upcoming scheduled maintenance, and invoices for their specific properties only, without being able to make bookings or see other properties in your portfolio.",
  },
];

/* ── Feature section ── */
function FeatureSection({ feature, index }) {
  const isEven = index % 2 === 0;
  return (
    <div style={{
      padding: "clamp(40px,6vw,64px) 0",
      borderBottom: `1px solid ${T.n100}`,
      display: "flex",
      flexDirection: "column",
      gap: 28,
    }}>
      {/* Label */}
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={feature.icon} size={15} color={T.g500} strokeWidth={1.8} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.g600, letterSpacing: "0.1em", textTransform: "uppercase" }}>{feature.label}</span>
      </div>

      {/* Headline + body */}
      <div style={{ maxWidth: 640 }}>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 14px", letterSpacing: "-0.025em", lineHeight: 1.15 }}>
          {feature.headline}
        </h3>
        <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.8, margin: 0 }}>{feature.body}</p>
      </div>

      {/* Detail checklist */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
        {feature.detail.map((d, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: 10, background: T.g50, border: `1px solid ${T.g100}` }}>
            <div style={{ flexShrink: 0, marginTop: 1 }}>
              <Icon name="CheckCircle" size={14} color={T.g500} strokeWidth={2.2} />
            </div>
            <span style={{ fontSize: 13, color: T.g700, lineHeight: 1.55, fontWeight: 500 }}>{d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Work step ── */
function WorkStep({ step, isLast }) {
  return (
    <div style={{ display: "flex", gap: 20, position: "relative" }}>
      {!isLast && (
        <div style={{ position: "absolute", left: 23, top: 52, bottom: -16, width: 1, background: `linear-gradient(to bottom, ${T.g200}, ${T.n100})` }} />
      )}
      <div style={{ width: 48, height: 48, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
        <Icon name={step.icon} size={20} color={T.g500} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 44 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", marginBottom: 5 }}>STEP {step.num}</div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(18px,2.8vw,22px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.2 }}>{step.title}</h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.78, margin: "0 0 14px" }}>{step.desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {step.chips.map((c, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "4px 11px", fontWeight: 500, fontFamily: T.sans }}>
              <Icon name="CheckCircle" size={12} color={T.g500} strokeWidth={2.2} />
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Testimonial ── */
function Testimonial({ name, role, location, text, stat }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("");
  const palette = [
    { bg: T.g50, text: T.g700, border: T.g100 },
    { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
    { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  ];
  const c = palette[name.charCodeAt(0) % palette.length];
  return (
    <div style={{ padding: 24, borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0, fontStyle: "italic" }}>"{text}"</p>
      <div style={{ borderTop: `1px solid ${T.n100}`, paddingTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: c.bg, border: `1.5px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, color: c.text, fontFamily: T.sans, flexShrink: 0 }}>{initials}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{name}</div>
            <div style={{ fontSize: 12, color: T.n400 }}>{role} · {location}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: T.serif, fontSize: 16, color: T.g600, fontWeight: 400, letterSpacing: "-0.02em" }}>{stat}</div>
        </div>
      </div>
    </div>
  );
}

/* ── FAQ item ── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "22px 0", display: "flex", justifyContent: "space-between",
        alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 500 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 22px", fontSize: 14, color: T.n600, lineHeight: 1.8, paddingRight: 48, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

/* ── Sticky CTA bar ── */
function StickyBar({ onSignUp }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
      background: T.white, borderTop: `1px solid ${T.n150}`,
      padding: "14px 20px", display: "flex", alignItems: "center",
      justifyContent: "space-between", gap: 12,
      boxShadow: "0 -4px 24px rgba(0,0,0,.08)",
      transform: visible ? "translateY(0)" : "translateY(100%)",
      transition: "transform .3s cubic-bezier(.4,0,.2,1)",
    }}>
      <div style={{ fontFamily: T.sans }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n900 }}>Ready to simplify your portfolio?</div>
        <div style={{ fontSize: 12, color: T.n400 }}>14-day free trial · No credit card required</div>
      </div>
      <button onClick={onSignUp} style={{
        padding: "11px 22px", background: T.g500, color: T.white,
        border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
        fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
        whiteSpace: "nowrap", flexShrink: 0,
      }}
        onMouseEnter={e => e.currentTarget.style.background = T.g600}
        onMouseLeave={e => e.currentTarget.style.background = T.g500}>
        Start free trial
      </button>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function PropertyManagementPage() {
  useFonts();
  const navigate = useNavigate();

  const goToSignUp  = () => navigate("/signup?type=property-manager");
  const goToContact = () => navigate("/contact?subject=property-management");
  const goToBusiness = () => navigate("/business");

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900, paddingBottom: 80 }}>
      <style>{`* { box-sizing: border-box; } ::-webkit-scrollbar { display: none; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={goToBusiness} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${T.n200}`, cursor: "pointer", color: T.n600, fontSize: 13, fontFamily: T.sans, padding: "8px 14px", borderRadius: 8, fontWeight: 500 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
            See all business plans
          </button>
          <button onClick={goToSignUp} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.g500, border: "none", cursor: "pointer", color: T.white, fontSize: 13, fontFamily: T.sans, padding: "8px 16px", borderRadius: 8, fontWeight: 600 }}
            onMouseEnter={e => e.currentTarget.style.background = T.g600}
            onMouseLeave={e => e.currentTarget.style.background = T.g500}>
            Start free trial
          </button>
        </div>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(56px,8vw,100px) 20px clamp(48px,6vw,80px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 24 }}>
            <Icon name="Building2" size={14} color={T.g500} strokeWidth={2.2} />
            For property managers
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(40px,7.5vw,68px)", fontWeight: 400, margin: "0 0 22px", lineHeight: 1.05, color: T.n900, letterSpacing: "-0.03em", maxWidth: 660 }}>
            Stop managing maintenance.<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>Start overseeing it.</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 16px", maxWidth: 540 }}>
            JiraniApp gives property managers a single platform to handle tenant maintenance requests, track jobs across every unit, schedule recurring work, and pay one invoice at month end.
          </p>
          <p style={{ fontSize: 14, color: T.n400, lineHeight: 1.65, margin: "0 0 36px", maxWidth: 500 }}>
            No more chasing contractors by phone. No more tenants calling your personal number at 9 PM. No more missing paperwork when a dispute arises.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={goToSignUp} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "15px 30px", borderRadius: 11, fontSize: 15, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start your free trial
              <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToContact} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "none", border: `1.5px solid ${T.n200}`, padding: "14px 24px", borderRadius: 11, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.n700 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Book a walkthrough
            </button>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
          {[
            { icon: "Building2",      label: "Unlimited properties under one account" },
            { icon: "Bell",           label: "Tenant requests handled without calling you" },
            { icon: "FileText",       label: "Full job audit trail, always downloadable" },
            { icon: "BadgeCheck",     label: "Business-tier verified JiraniPros only" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "20px 16px", display: "flex", alignItems: "center", gap: 11, borderRight: i < 3 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={16} color={T.g500} strokeWidth={1.6} />
              <span style={{ fontSize: 12, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>

        {/* ══ PAIN POINTS ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.n400, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>The problem</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Managing maintenance is a <em style={{ fontStyle: "italic", color: T.red }}>full-time job on its own</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Most property managers are running their portfolio on a combination of phone calls, WhatsApp groups, and memory. It works until it does not.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {PAIN_POINTS.map((p, i) => (
              <div key={i} style={{ padding: "20px", borderRadius: 14, border: `1px solid ${T.redBorder}`, background: T.redBg, display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: "#fee2e2", border: `1px solid ${T.redBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={p.icon} size={16} color={T.red} strokeWidth={1.7} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#991b1b", marginBottom: 5 }}>{p.label}</div>
                  <p style={{ fontSize: 13, color: "#b91c1c", lineHeight: 1.65, margin: 0 }}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FEATURES ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>How JiraniApp solves it</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Four features built for <em style={{ fontStyle: "italic", color: T.g600 }}>property managers</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 0", lineHeight: 1.65 }}>
            Not features bolted onto a consumer product. Built specifically around how property maintenance actually works.
          </p>
          <div>
            {FEATURES.map((f, i) => <FeatureSection key={i} feature={f} index={i} />)}
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Getting started</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Full setup in <em style={{ fontStyle: "italic", color: T.g600 }}>under an hour</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 36px", lineHeight: 1.65 }}>
            Add your properties, share the tenant request links, set up your recurring schedules, and your portfolio is running on JiraniApp. Most property managers are fully operational the same day they sign up.
          </p>
          <div style={{ paddingLeft: 4 }}>
            {HOW_IT_WORKS.map((step, i) => (
              <WorkStep key={i} step={step} isLast={i === HOW_IT_WORKS.length - 1} />
            ))}
          </div>
          <div style={{ marginTop: 36, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={goToSignUp} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "13px 26px", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start your free trial
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <span style={{ fontSize: 13, color: T.n400 }}>14 days free, no card required</span>
          </div>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>From property managers</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            In their own <em style={{ fontStyle: "italic", color: T.g600 }}>words</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            From managers and landlords already running their portfolios on JiraniApp.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 14 }}>
            {TESTIMONIALS.map((t, i) => <Testimonial key={i} {...t} />)}
          </div>
        </div>

        {/* ══ AUDIT TRAIL CALLOUT ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)", padding: "clamp(28px,5vw,44px)", borderRadius: 20, background: T.n50, border: `1px solid ${T.n150}` }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.n600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 14 }}>
            <Icon name="Lock" size={13} color={T.n400} strokeWidth={2} />
            Documentation and audit
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            A full record of every job, <em style={{ fontStyle: "italic", color: T.g600 }}>for as long as you need it</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n600, lineHeight: 1.75, margin: "0 0 28px" }}>
            Property management disputes happen. A tenant claims something was never repaired. A landlord questions what was spent and when. With JiraniApp, every job generates a timestamped, tamper-proof record that covers everything you need to resolve it quickly.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {[
              { icon: "Clock",       title: "Arrival and completion timestamps", desc: "Exact times recorded automatically when the JiraniPro marks arrival and job complete." },
              { icon: "User",        title: "Verified Pro identity on every record", desc: "Every job record carries the JiraniPro's verified name and ID number." },
              { icon: "Key",         title: "Job description and scope", desc: "The original booking details and any notes added by the Pro during the job are permanently attached." },
              { icon: "BarChart2",   title: "Photo documentation", desc: "JiraniPros upload before and after photos on completion. These are stored on your dashboard indefinitely." },
              { icon: "FileText",    title: "Downloadable as PDF", desc: "Any job record or full property maintenance log can be exported as a PDF at any time." },
              { icon: "TrendingUp",  title: "Read-only owner access", desc: "Share specific property records with landlords or auditors without giving them booking permissions." },
            ].map((item, i) => (
              <div key={i} style={{ padding: "16px", borderRadius: 12, background: T.white, border: `1px solid ${T.n150}`, display: "flex", flexDirection: "column", gap: 9 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: T.n50, border: `1px solid ${T.n150}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={15} color={T.n600} strokeWidth={1.7} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.n900, letterSpacing: "-0.01em" }}>{item.title}</div>
                <p style={{ fontSize: 12, color: T.n500, lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What property managers <em style={{ fontStyle: "italic", color: T.g600 }}>ask us</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Straight answers to the practical questions that come up most.
          </p>
          <div>
            {FAQS.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,56px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50, marginBottom: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Building2" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(28px,4.5vw,40px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Your portfolio, finally<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>under control</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 32px", lineHeight: 1.75, maxWidth: 420 }}>
            Start your free 14-day trial. Add your properties, share your tenant links, and see what maintenance management looks like when it actually works.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={goToSignUp} style={{
              padding: "14px 32px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Start free trial
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={goToContact} style={{
              padding: "14px 28px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Book a walkthrough
            </button>
          </div>
          <p style={{ fontSize: 12, color: T.n300, margin: "20px 0 0" }}>
            14-day free trial · No credit card required · Cancel any time
          </p>
        </div>

      </div>

      <StickyBar onSignUp={goToSignUp} />
    </div>
  );
}